<template>
    <b-container fluid class="flex-grow-1 d-flex flex-column p-0">
        <div class="p-3">
            <section class="grid-filters">
                <b-row align-h="between" class="mb-3">
                    <b-col
                            cols="12"
                            sm="5"
                            md="4"
                            class="order-0 d-sm-block mb-0"
                    >
                        <search-filter
                                :showSearch="true"
                                v-model="query.search"
                        >
                        </search-filter>
                    </b-col>
                    <b-col
                            cols="12"
                            md="2"
                            class="mt-2"
                    >
                        <div class="text-right">
                            <b-btn variant="primary"
                                   @click="openModal('upload')"
                            >
                                <font-awesome-icon
                                        class="mr-2"
                                        :icon="['fas','cloud-upload-alt']"
                                >
                                </font-awesome-icon>
                                Upload files
                            </b-btn>
                        </div>
                    </b-col>
                </b-row>
            </section>

            <b-row class="flex-grow-1">
                <b-col class="h-100 d-flex flex-column">
                    <b-row class="grid-count mb-3 justify-content-between align-items-baseline">
                        <b-col cols="8" class="d-flex align-items-baseline text-dark">
                            <span>
                                {{ count }} Results
                                <span class="font-weight-bold"
                                      v-if="selectedCount > 0"
                                >
                                    | {{ selectedCount }} selected
                                </span>
                            </span>
                        </b-col>
                        <b-col cols="4" class="d-flex justify-content-end">
                            <reset-filters
                                    @click="resetGridToDefaultOptions"></reset-filters>
                            <refresh-button
                                    :spinning="refreshing"
                                    @click="refresh"
                            ></refresh-button>
                        </b-col>
                    </b-row>
                    <div class="grid-wrapper flex-grow-1">
                        <ag-grid-vue
                                :style="'width: ' + gridWidth +'%; height: 76vh;'"
                                class="ag-theme-balham"

                                :gridOptions="gridOptions"
                                :defaultColDef="defaultColDef"
                                :columnDefs="columnDefs"
                                :rowData="rowData"
                                :columnTypes="columnTypes"
                                :rowSelection="rowSelection"

                                :side-bar="sideBarDef"
                                @gridReady="onGridReady"
                                :floatingFilter="floatingFilter"
                                :quickFilter="query.search"
                                :cacheQuickFilter="cacheQuickFilter"

                                :getContextMenuItems="getContextMenuItems"
                                :suppressExcelExport="suppressExcelExport"

                                :rowSelected="updateRowSelected"

                                :headerHeight="rowHeight"
                                :rowHeight="rowHeight"
                                @cellClicked="onCellClicked"

                                @columnMoved="saveGridOptions"
                                @columnPinned="saveGridOptions"
                                @columnResized="saveGridOptions"
                                @columnVisible="saveGridOptions"
                                @filterChanged="saveGridOptions"
                                @sortChanged="saveGridOptions"
                        >
                        </ag-grid-vue>
                    </div>
                </b-col>
            </b-row>
        </div>
        <file-modal
                v-if="modals.upload.visible"
                :file-url="fileUrl"
                :show.sync="modals.upload.visible"
                @hidden="hideModal('upload')"
                :options="dropOptions"
                :form="modals.upload.form"
                @all-files-sent="onAllFilesSent"
        >
        </file-modal>
    </b-container>
</template>


<script>
import { AgGridVue } from 'ag-grid-vue';
import {
  clientFileGridColumnTypes as gridColumnTypes,
  clientFileDefaultColDef as gridDefaultColDef,
  clientFileGridColumns as gridColumns,
} from '@/services/Client/ClientFileListingGrid';
import GridMixin from '@/components/Grid/GridMixin';
import ResetFilters from '@/components/Grid/ResetFilters';
import RefreshButton from '@/components/Grid/RefreshButton';
import SearchFilter from '@/components/Grid/SearchFilter';
import ClientFile from '@/services/Client/ClientFile';
import { mapGetters } from 'vuex';
import HasModalsMixin from '@/components/Modal/HasModalsMixin';
import FileModal from '@/components/Modal/FileModal';
import api from '@/api';

export default {
  name: 'File-Sharing',
  components: {
    AgGridVue,
    ResetFilters,
    RefreshButton,
    SearchFilter,
    FileModal,
  },
  mixins: [GridMixin, HasModalsMixin],
  data() {
    return {
      modelId: 'fileId',
      query: {
        search: '',
      },
      defaultColDef: gridDefaultColDef,
      columnDefs: gridColumns,
      columnTypes: gridColumnTypes,
      gridName: 'file-sharing',
      dropOptions: {
        uploadMultiple: false,
        acceptedFiles: '.csv,.xls,.xlsx,.doc,.docx,.png,.jpg,.jpeg,.ppt,.pptx,.pptm,.pps,.ppsx,.eml,.xml,.txt,.pdf,.zip',
        maxFilesize: 500,
        dictFileTooBig: 'File is too big ({{filesize}}MB). Max filesize: {{maxFilesize}}MB.',
      },
      modals: {
        upload: {
          visible: false,
          form: {
            description: {
              label: 'Add description',
              type: 'textarea',
              value: '',
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({ clientId: 'userClientId' }),
    fileUrl() {
      return `${api.defaults.baseURL}${ClientFile.getCreateUrl(this.clientId)}`;
    },
  },

  methods: {
    postGridReady(params) {
      this.refresh();
      this.gridApi.sizeColumnsToFit();
    },

    /**
       * Async
       * Refreshes the table
       * Sets the grid options afterwards
       */
    async refresh() {
      this.refreshing = true;
      // Wait until the data is set, external dropdown filters are set
      await this.refreshData();
      // Then we set the grid options
      // We set a timeout to disallow any future grid saving that happens
      // As filters are applied immediately after;
      setTimeout(() => {
        this.refreshing = false;
      }, 1);

      // Then we set the grid options
      this.setGridOptions();

      this.postRefresh();
    },

    /**
       * Call after every refresh
       * */
    postRefresh() {
      this.updateRowSelected();
    },

    /**
       * Sets the rowdata with data from the server
       */
    async refreshData() {
      this.refreshing = true;

      try {
        const { data } = await ClientFile.list(this.clientId);
        this.rowData = data.data;
      } catch (e) {
        this.$notify({
          duration: 5000,
          title: 'Error',
          text: 'We couldn\'t obtain the files right now. Please try again later.',
          type: 'error',
        });
      }
    },

    onAllFilesSent() {
      this.$notify({
        type: 'success',
        title: 'Success',
        text: 'File(s) have been uploaded',
      });
      this.refresh();
    },

    hideModal(name) {
      this.modals[name].visible = false;

      if (name === 'upload') {
        this.modals[name].form.description.value = '';
      }
    },
  },
};
</script>

<style lang="scss">

</style>
